/* eslint-disable no-underscore-dangle */

const _ = require('lodash');
const CollectionAttribute = require('./../index');
const Range = require('../../../NumericValues/Range');

function validate(config) {
  if (_.isNil(config)) {
    throw new Error('config must not be null or undefined.');
  }

  if (_.isEmpty(config.attributeRanges)) {
    throw new Error('attributeRanges are required');
  }
}

module.exports = class AttributeConstraintRanges extends CollectionAttribute {
  /**
   * See parent for details.
   * @param  {Object[]} config.attributeRanges A collection of attributeRanges that are valid for this attribute.
   */
  constructor(config) {
    validate(config);
    super(config);

    this._attributeRanges = config.attributeRanges;
    this._type = CollectionAttribute.Types.listOfRanges;
  }

  /**
   * Does the given value overlap with this attribute's values
   * @param  {string|Range} value The value to check against
   * @return {bool} true if and only if the given value overlaps with this attribute's values
   */
  overlaps(value) {
    return _.some(this._attributeRanges, (attributeRange) => {
      if (value instanceof Range) {
        return attributeRange.overlaps(value);
      }

      return attributeRange.contains(value);
    });
  }

  /**
   * Does this attribute contain the given value
   * @param {string|Range} value The value to check against
   * @return {bool} true if and only if the given value is contained by this attribute's values
   */
  contains(value) {
    return _.some(this._attributeRanges, attributeRange => attributeRange.contains(value));
  }

  /**
   * A range attribute is implied if there is only one possible value.
   */
  canBeImplied() {
    const singularOptions = [];

    const rangeValuesHaveSingularOptions = _.every(this.values, (value) => {
      const hasSingleOption = this._rangeValueHasSingleOption(value);

      if (hasSingleOption) {
        singularOptions.push(value.minimum);
      }

      return hasSingleOption;
    });

    return rangeValuesHaveSingularOptions && _.uniq(singularOptions).length === 1;
  }

  /**
   * Helper method to check if a range value has a singular option
   *
   * If a range has a min/max that are the same, it has a singular option
   * If a range's minimum + increment is greater than the maximum, it has a singular option
   *
   * i.e. 1-10#10 will list 1 as the singular value
   * @returns true if there is one singular option
   */
  // eslint-disable-next-line class-methods-use-this
  _rangeValueHasSingleOption(rangeValue) {
    if (_.isNil(rangeValue.maximum)) {
      return false;
    }

    if (_.isNil(rangeValue.increment) && rangeValue.minimum === rangeValue.maximum) {
      return true;
    }

    return rangeValue.minimum + rangeValue.increment > rangeValue.maximum;
  }

  get values() {
    return this._attributeRanges;
  }

  /**
   * Returns the valid options for a range attribute
   *
   * If the range is implied, we'll return the only valid option back as a Range model object
   * @readonly
   * @returns <Array<Range>> - Set of valid options in the form of Range model objects
   */
  get validOptions() {
    if (!this.canBeImplied()) {
      return this._attributeRanges;
    }

    const impliedValue = _.first(this._attributeRanges).minimum;
    const impliedRange = new Range(impliedValue, impliedValue, impliedValue);

    return [impliedRange];
  }
};
